import ReactDOM from 'react-dom';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { IForm, IFormContentReference, ISpace } from '@typedream/data';
import { Serializer } from '@typedream/components';
import { MoreHoriz, Edit, TableRows, Delete } from '@styled-icons/material-outlined';
import { offset } from '@floating-ui/react-dom';
import {
  useDismiss,
  useFloating,
  useFloatingPortalNode,
  useInteractions,
} from '@floating-ui/react';
import styled from 'styled-components';
import { LOGO_DOMAIN } from '@/constants';
import { Link } from 'phosphor-react';
import { toast } from 'react-toastify';
import { copyToClipboard } from '@/utils/copyToClipboard';
import {
  FormPreviewCardHeader,
  StyledFormContent,
  StyledFormContentRow,
  StyledFormPreviewCardContainer,
  StyledFormPreviewCardIcon,
} from './components';
import isContentEqualNoID from '../../utils/isContentEqualNoID';

const StyledFormName = styled.div`
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-right: 5px;
  color: var(--text);
`;

const StyledPreviewContent = styled.div`
  width: 153%;
  position: relative;
  top: 0;
  transform: scale(0.5);
  transform-origin: top left;
  margin-right: -73%;
  margin-bottom: -200%;
`;

function CustomThreeDots({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [randomID, setrandomID] = React.useState('id'); // random portal Node ID
  const originalPointerStyle = React.useRef(''); // document.body.style.pointerEvents;
  const { x, y, reference, floating, strategy, context } = useFloating({
    open,
    onOpenChange: (o) => {
      setOpen(o);
      document.body.style.pointerEvents = originalPointerStyle.current;
    },
    middleware: [
      offset({
        mainAxis: -15,
        crossAxis: -15,
      }),
    ],
    placement: 'bottom-end',
  });

  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  useEffect(() => {
    const r = (Math.random() + 1).toString(36).substring(7);
    setrandomID(r);
  }, []);

  // Without a random portal node id, if there's a Delete option inside here that will cause
  // this particular CustomThreeDots instant to be deleted from the DOM, the portal node
  // will be deleted as well, causing all other CustomThreeDots's portalNodes to be deleted too.
  const portalNode = useFloatingPortalNode({ id: `form-preview-card-three-dots-${randomID}` });

  return (
    <>
      <StyledFormPreviewCardIcon
        data-state-open={open}
        role="none"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          originalPointerStyle.current = document.body.style.pointerEvents;
          document.body.style.pointerEvents = '';
        }}
        ref={reference}
        {...getReferenceProps()}
      >
        <MoreHoriz size={20} style={{ cursor: 'pointer' }} />
      </StyledFormPreviewCardIcon>
      {portalNode &&
        open &&
        ReactDOM.createPortal(
          <div
            ref={floating}
            style={{
              top: y ?? 0,
              left: x ?? 0,
              position: strategy,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--color-surface-raised)',
              padding: 10,
              borderRadius: 6,
              color: 'var(--text)',
              boxShadow: '0px 8px 104px -2px rgba(16, 24, 40, 0.1)',
            }}
            {...getFloatingProps()}
          >
            {children}
          </div>,
          portalNode
        )}
    </>
  );
}

function fmtFormLastModified(formID: string, formReferences: IFormContentReference): string {
  if (!formReferences || !formReferences.content) {
    return '';
  }

  const lastModifiedForm = formReferences.content[formID];
  if (!lastModifiedForm) return '';

  const d = new Date(lastModifiedForm.updated_at);
  return `Last Modified at ${d.toDateString()}`;
}

function fmtFormLastPublished(formID: string, formReferences: IFormContentReference): string {
  if (!formReferences || !formReferences.published_content) {
    return fmtFormLastModified(formID, formReferences);
  }

  const lastPublishedForm = formReferences.published_content[formID];
  if (!lastPublishedForm) return fmtFormLastModified(formID, formReferences);

  const d = new Date(lastPublishedForm.updated_at);
  return `Last Published at ${d.toDateString()}`;
}

export function FormPreviewCard({
  id,
  form: f,
  space,
  handleDelete,
  setSelectedFormID,
  selectedFormID,
  onHandleEditOption,
  onHandleFormSubmissionOption,
  onClick,
}: {
  id: string;
  form: IForm;
  space: ISpace;
  handleDelete: (formID: string) => Promise<void>;
  setSelectedFormID: Dispatch<SetStateAction<string>>;
  selectedFormID: string;
  onHandleEditOption: () => void;
  onHandleFormSubmissionOption: () => void;
  onClick?: () => void;
}) {
  // TODO: subdomain or custom domain?
  const typedreamSubdomain = `${space.slug}.${LOGO_DOMAIN}`;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const formReference = space?.customization?.form_reference || {};
  const publishedContent = formReference.published_content?.[id]?.value || [];
  const currentContent = formReference.content?.[id]?.value || [];
  const isDraft =
    publishedContent.length === 0 || !isContentEqualNoID(publishedContent, currentContent);

  const formContent = formReference.content?.[id]?.value || [];

  const isFormExist = formContent && formContent?.[0];

  return (
    <StyledFormPreviewCardContainer
      ref={containerRef}
      selected={selectedFormID === f.id}
      role="none"
      onClick={() => {
        setSelectedFormID(f.id);
        onClick?.();
      }}
    >
      <FormPreviewCardHeader>
        {isFormExist ? (
          <StyledPreviewContent>
            <Serializer
              rootClassName="typedream content"
              node={(formContent?.[0] || { children: [] }) as any}
            />
          </StyledPreviewContent>
        ) : (
          <div className="with-text-color has-text-weight-semibold">Empty Form</div>
        )}
      </FormPreviewCardHeader>
      <StyledFormContent>
        <StyledFormContentRow>
          <StyledFormName>{f.name}</StyledFormName>
          {!!isDraft && (
            <div
              className="with-accent-text-color has-text-weight-semibold"
              style={{
                display: 'grid',
                placeItems: 'center',
                padding: '0px 6px',
                borderRadius: 4,
                backgroundColor: '#EFEFFF',
                fontSize: 10,
              }}
            >
              DRAFT
            </div>
          )}
        </StyledFormContentRow>
        <div className="with-text-color is-size-7" style={{ textOverflow: 'ellipsis' }}>
          {fmtFormLastPublished(f.id, formReference || {})}
        </div>
        <CustomThreeDots>
          <div
            role="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 5,
              fontSize: 14,
              verticalAlign: 'center',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onHandleEditOption();
            }}
          >
            <Edit size={18} style={{ marginRight: 5 }} />
            Edit Form
          </div>
          {!isDraft && (
            <div
              role="none"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: 5,
                fontSize: 14,
                verticalAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(`https://${typedreamSubdomain}/f/${f.id}`, containerRef.current);
                toast.success('Copied to clipboard');
              }}
            >
              <Link size={18} style={{ marginRight: 5 }} />
              Share Link
            </div>
          )}
          <div
            role="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 5,
              fontSize: 14,
              verticalAlign: 'center',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onHandleFormSubmissionOption();
            }}
          >
            <TableRows size={18} style={{ marginRight: 5 }} />
            Form Submission
          </div>
          {/* <div
            role="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 5,
              fontSize: 14,
              verticalAlign: 'center',
            }}
          >
            <Label size={18} style={{ marginRight: 5 }} />
            Rename
          </div>
          <div
            role="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 5,
              fontSize: 14,
              verticalAlign: 'center',
            }}
          >
            <Copy size={18} style={{ marginRight: 5 }} />
            Duplicate
          </div> */}
          <div
            role="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 5,
              fontSize: 14,
              color: 'var(--danger)',
              verticalAlign: 'center',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(f.id);
            }}
          >
            <Delete size={18} style={{ marginRight: 5 }} />
            Delete
          </div>
        </CustomThreeDots>
      </StyledFormContent>
    </StyledFormPreviewCardContainer>
  );
}
